/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';

import { setCurentStep, setDrawerCloseDisabled } from 'api/client-side-slices/DesignSlice';
import { useGetEntityRolesQuery } from 'api/EntitiesApiSlice';
import { useAddIndividualMutation } from 'api/IndividualsApiSlice';
import { useAppDispatch, useAppSelector } from 'app-redux/hooks';
import { useModal } from 'components';
import { useDrawer } from 'components/Drawer/Drawer';
import { ModalComponent } from 'components/Modal/Modal';
import { format } from 'date-fns';
import { useToast } from 'providers/ToastProvider';
import { Owner } from 'solstice-types';
import Cookies from 'universal-cookie';
import { parseAddress, removeWhiteSpaces } from 'utils';
import { IndividualDrawerFooter } from '../AddNewIndividual/IndividualDrawerFooter';
import { AddCompanyIndividualsStep } from './Steps/AddCompanyIndividualsStep';
import { AddCompanyStep } from './Steps/AddCompanySteps';
import { EntitySearchStep } from './Steps/EntitySearchStep';

enum Step {
  SEARCH,
  ADD_COMPANY,
  ADD_INDIVIDUALS,
  REVIEW,
}

export interface OwnerWithEmail extends Owner {
  email?: string;
}

enum SearchType {
  KYC = 'KYC',
  Prime = 'Prime',
}

export const CompanyVerificationFlow = () => {
  const [currentStep, setCurrentStep] = useState<Step>(Step.SEARCH);
  const [stepHistory, setStepHistory] = useState<Step[]>([Step.SEARCH]);
  const [forceStorePerson, setForceStorePerson] = useState<boolean>(false);
  const [showFooter, setShowFooter] = useState(true);
  const [searchType, setSearchType] = useState<SearchType | undefined>(undefined);
  const { user, isAuthenticated } = useAuth0();

  const { data: entityRoles } = useGetEntityRolesQuery({ type: 'Company' });
  const defaultRoleId = entityRoles?.find((role) => role.name === 'Other')?.id;
  const [addIndividuals, { isLoading: areIndividualsLoading }] = useAddIndividualMutation();
  const toast = useToast();

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, getValues, reset } = methods;

  const { closeDrawer } = useDrawer();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { existingCompany, uboReportData, abnIdentifier } = useAppSelector((state) => state.groups);

  const { openModal, closeModal } = useModal();

  const modalBodyText =
    'You are about to cancel and all progress will be lost. Any data you entered for the new company will be lost. Are you sure you want to proceed?';

  const duplicateEmailText =
    'This email address is already used by another individual in your Prime KYC account. Cancel to change the email, or create a new verification using it.';

  const preventVerificationText = (
    <>
      To register for Prime KYC access, please contact{' '}
      <a href="mailto:support@primecc.com.au" className="text-blue-500">
        support@primecc.com.au
      </a>
    </>
  );

  const handleAsicSearchSelection = () => {
    openModal(
      <ModalComponent
        bodyContent={
          <div>
            ASIC Searches are a single level search and may not fulfill your compliance obligations
            in identifying ultimate beneficial owners. To run a recursive UBO report, please select
            the Run UBO option. <br />
            <br /> Are you sure you wish to continue with a Single Level ASIC Search?
          </div>
        }
        onCancel={closeModal}
        onUpdate={() => {
          goToNextStep(Step.ADD_INDIVIDUALS);

          closeModal();
        }}
        buttonText="Continue"
      />,
      'ASIC Search'
    );
  };

  const handleUpdate = () => {
    setForceStorePerson((prev) => {
      const newValue = !prev;

      setTimeout(() => {
        handleSave(newValue);
      }, 2000);

      return newValue;
    });

    closeModal();
  };

  const handleCloseModal = () => {
    setCurrentStep(0);
    dispatch(setCurentStep(0));
    dispatch(setDrawerCloseDisabled(true));
    reset();
    closeModal();
  };

  const handleDisplayModal = () => {
    openModal(
      <ModalComponent
        bodyContent={modalBodyText}
        onCancel={closeModal}
        onUpdate={handleCloseModal}
        buttonText="Confirm"
      />,
      'Are You Sure?'
    );
  };

  const goToPreviousStep = () => {
    openModal(
      <ModalComponent
        bodyContent={modalBodyText}
        onCancel={closeModal}
        onUpdate={() => {
          setCurrentStep(1);
          dispatch(setCurentStep(1));
          dispatch(setDrawerCloseDisabled(true));
          reset();
          closeModal();
        }}
        buttonText="Confirm"
      />,
      'Are You Sure?'
    );
  };

  const goToNextStep = async (nextStep: Step) => {
    setCurrentStep(nextStep);
    dispatch(setCurentStep(nextStep));
    setStepHistory((prevHistory) => [...prevHistory, nextStep]);
  };

  const onSelectPrime = (cb?: () => void) => {
    setSearchType(SearchType.Prime);
    cb?.();
  };

  const onSelectKYC = (cb?: () => void) => {
    setSearchType(SearchType.KYC);
    cb?.();
  };

  const renderStep = () => {
    switch (currentStep) {
      case Step.SEARCH:
        return (
          <EntitySearchStep
            setStep={goToNextStep}
            onSelectPrime={onSelectPrime}
            onSelectKYC={onSelectKYC}
          />
        );
      case Step.ADD_COMPANY:
        return (
          <AddCompanyStep
            handleDisplayModal={handleDisplayModal}
            existingCompany={existingCompany}
          />
        );
      case Step.ADD_INDIVIDUALS:
        return (
          <AddCompanyIndividualsStep
            setStep={goToNextStep}
            methods={methods}
            entityRoles={entityRoles}
            isExistingCompany={!!existingCompany}
            handleDisplayModal={handleDisplayModal}
            setShowFooter={setShowFooter}
          />
        );
      default:
        return null;
    }
  };

  const getEntityData = (index: number, values: Record<string, any>) => {
    const address = values[`address-${index}`] || {};

    return {
      ...address,
      dateOfBirth: values[`dateOfBirth-${index}`] || '',
    };
  };

  const mappedOwners = (
    Array.isArray(uboReportData)
      ? uboReportData
      : uboReportData?.report?.ubo?.beneficialOwners?.owners || []
  ).map((owner: any, i: number) => {
    const values = getValues();
    const entityData = getEntityData(i, values);

    const {
      name: {
        firstName: nameFirstName = '',
        middleName: nameMiddleName = '',
        lastName: nameLastName = '',
      } = {},
      firstName,
      middleName,
      lastName,
      dateOfBirth,
      address,
      addressInput,
      ownership,
      roleName,
      roleId,
      ownershipPercentage,
      mobilePhone,
      sharesBeneficiallyHeld,
      email,
      country,
      postcode,
      state,
      streetName,
      streetNumber,
      streetType,
      suburb,
    } = owner ?? {};

    const { entityId: reportEntityId } = uboReportData ?? {};
    const { entityId: abnEntityId } = abnIdentifier ?? {};

    const {
      country: parsedCountry,
      postcode: parsedPostcode,
      state: parsedState,
      streetName: parsedStreetName,
      streetNumber: parsedStreetNumber,
      streetType: parsedStreetType,
      suburb: parsedSuburb,
    } = parseAddress(address || '');

    const {
      country: entityCountry,
      postcode: entityPostcode,
      state: entityState,
      streetName: entityStreetName,
      streetNumber: entityStreetNumber,
      streetType: entityStreetType,
      suburb: entitySuburb,
      dateOfBirth: entityDateOfBirth,
    } = entityData;

    const {
      country: beneficialCountry,
      postcode: beneficialPostcode,
      state: beneficialState,
      streetName: beneficialStreetName,
      streetNumber: beneficialStreetNumber,
      streetType: beneficialStreetType,
      suburb: beneficialSuburb,
    } = addressInput ?? {};

    const ownerRoleId =
      entityRoles?.find((role) => role.name === roleName)?.id || roleId || defaultRoleId;
    const formatDateOfBirth = (dob: string | undefined): string =>
      dob && dob !== '0001-01-01' && !isNaN(new Date(dob).getTime())
        ? format(new Date(dob), 'yyyy-MM-dd')
        : '';
    return {
      firstName: nameFirstName || firstName,
      middleName: nameMiddleName || middleName,
      lastName: nameLastName || lastName,
      dateOfBirth: formatDateOfBirth(dateOfBirth) || formatDateOfBirth(entityDateOfBirth),
      entityId: reportEntityId || abnEntityId || '',
      ownershipPercentage: Number(ownership) || Number(ownershipPercentage),
      mobilePhone: removeWhiteSpaces(mobilePhone || ''),
      roleId: ownerRoleId,
      forceStore: true,
      sharesBeneficiallyHeld,
      email,
      country: country || parsedCountry || beneficialCountry || entityCountry,
      postcode: postcode || parsedPostcode || beneficialPostcode || entityPostcode,
      state: state || parsedState || beneficialState || entityState,
      streetName: streetName || parsedStreetName || beneficialStreetName || entityStreetName,
      streetNumber:
        streetNumber || parsedStreetNumber || beneficialStreetNumber || entityStreetNumber,
      streetType: streetType || parsedStreetType || beneficialStreetType || entityStreetType,
      suburb: suburb || parsedSuburb || beneficialSuburb || entitySuburb,
    };
  });

  const handleSave = async (forceStoreOverride?: boolean) => {
    const forceStore = forceStoreOverride ?? forceStorePerson;
    const formValues = getValues();

    if (existingCompany && !searchType) {
      toast.error('Please select an entity');
      return;
    }

    if (currentStep === Step.SEARCH && searchType === SearchType.KYC && existingCompany) {
      goToNextStep(Step.ADD_INDIVIDUALS);
    } else if (currentStep === Step.SEARCH && searchType === SearchType.Prime && existingCompany) {
      goToNextStep(Step.ADD_COMPANY);
    } else if (currentStep === Step.ADD_COMPANY && formValues.apiCheckSelect === 'asicSearch') {
      handleAsicSearchSelection();
    } else if (currentStep === Step.ADD_COMPANY && formValues.apiCheckSelect === 'uboCheck') {
      goToNextStep(Step.ADD_INDIVIDUALS);
    } else {
      const requiredFields = [
        'firstName',
        'lastName',
        'email',
        'ownershipPercentage',
        'entityId',
        'roleId',
        'dateOfBirth',
      ];

      const formattedData = Object.keys(formValues)
        .filter((key) => key.startsWith('firstName_'))
        .map((key) => {
          const i = key.split('_')[1];
          const dateOfBirth = formValues[`dateOfBirth_${i}`];
          const addressComponents = formValues[`address_${i}`];
          const { streetNumber, streetName, streetType, suburb, state, postcode, country } =
            addressComponents || {};
          return {
            firstName: formValues[`firstName_${i}`] || '',
            lastName: formValues[`lastName_${i}`] || '',
            middleName: formValues[`middleName_${i}`] || '',
            email: formValues[`email_${i}`] || '',
            mobilePhone: removeWhiteSpaces(formValues[`mobilePhone_${i}`] || ''),
            dateOfBirth: dateOfBirth ? format(new Date(dateOfBirth), 'yyyy-MM-dd') : '1900-01-01',
            ownershipPercentage: Number(formValues[`ownership_${i}`]) || 0,
            entityId: uboReportData?.entityId || abnIdentifier.entityId || '',
            roleId: formValues[`role_${i}`] || '',
            forceStore: forceStore,
            streetNumber: streetNumber || '',
            streetName: streetName || '',
            streetType: streetType || '',
            suburb: suburb || '',
            state: state || '',
            postcode: postcode || '',
            country: country || '',
          };
        })
        .filter((data) => data !== undefined);

      if (formattedData.length === 0 && mappedOwners.length === 0) {
        toast.error('No valid individuals to add. Please fill the fields.');
        return;
      }

      try {
        const cookie = new Cookies();
        const token = cookie.get('token');
        const greenId = cookie.get('greenId');
        if ((token && !greenId) || (isAuthenticated && !user?.user_metadata?.GreenId)) {
          openModal(
            <ModalComponent
              bodyContent={preventVerificationText}
              onCancel={closeModal}
              hasOneButton={true}
              buttonText="Close"
              onUpdate={() => {
                closeModal();
              }}
            />,
            'Verification failed'
          );
          return;
        }

        await addIndividuals({
          newIndividual: [...formattedData, ...mappedOwners],
          isGroup: true,
        })
          .unwrap()
          .then(() => {
            navigate(`/entity-details/${uboReportData?.entityId ?? abnIdentifier.entityId}`);

            toast.success('Link generated successfully!');
            closeDrawer();
          });
      } catch (error: any) {
        let errorMessage = '';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null && 'data' in error) {
          const { data } = error as { data: string | object };
          errorMessage =
            typeof data === 'string' ? data.toLowerCase().trim() : JSON.stringify(data, null, 2);
        }

        if (errorMessage.includes('person with email:')) {
          openModal(
            <ModalComponent
              bodyContent={duplicateEmailText}
              isLoading={areIndividualsLoading}
              onCancel={closeModal}
              onUpdate={handleUpdate}
            />,
            'Duplicated Email'
          );
        } else if (errorMessage.includes('to register for prime kyc access')) {
          openModal(
            <ModalComponent
              bodyContent={preventVerificationText}
              onCancel={closeModal}
              hasOneButton={true}
              buttonText="Close"
              onUpdate={() => {
                closeModal();
              }}
            />,
            'Verification failed'
          );
        } else if (errorMessage.includes('was not found in this key vault')) {
          openModal(
            <ModalComponent
              bodyContent={preventVerificationText}
              onCancel={closeModal}
              hasOneButton={true}
              buttonText="Close"
              onUpdate={() => {
                closeModal();
              }}
            />,
            'Verification failed'
          );
        } else {
          toast.error('A general error has occurred, please contact IT support for assistance.');
        }
      }
    }
  };

  const renderButtonText = () =>
    currentStep === Step.ADD_INDIVIDUALS ? 'Start Verification' : 'Continue';

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(() => handleSave())}>
        {renderStep()}
        <IndividualDrawerFooter
          confirmButtonText={renderButtonText}
          handleSave={handleSubmit(() => handleSave(forceStorePerson))}
          handlePreviousStep={goToPreviousStep}
          hasSteps={currentStep === Step.ADD_INDIVIDUALS}
          isLoading={areIndividualsLoading}
          hideFooter={!showFooter || currentStep === Step.REVIEW}
        />
      </form>
    </FormProvider>
  );
};
